import { Avatar, Box, Typography, useTheme } from "@mui/material";
import { InboxData } from "../operation/inbox/InboxList";
import { useNavigate } from "react-router-dom";
import { buildAvatarSrc, formatInboxTime, getInitials } from "../../utils/Utility";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";

type InboxTab = "unread" | "read" | "sent";

interface CommunicationInboxPanelProps {
    notifTab: InboxTab;
    onTabChange: (tab: InboxTab) => void;
    inboxData: InboxData[],
    tabArray: InboxTab[],
    isDashboardColor?: boolean,
    maxRows?: number;
    hideProfileImage?: boolean;
    headerAdornment?: React.ReactNode;
}

export const CommunicationInboxPanel = ({
    notifTab,
    onTabChange,
    inboxData,
    tabArray,
    isDashboardColor,
    maxRows,
    hideProfileImage,
    headerAdornment,
}: CommunicationInboxPanelProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isDark = theme.palette.mode === "dark";
    const cardBg = isDark ? "#191c24" : "#ffffff";
    const borderColor = isDark ? "rgba(255,255,255,0.07)" : "#e5e9ed";
    const rowBorder = isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9";
    const avatarColors = ["#0d9488", "#1e6091", "#6d3fc0", "#b45309", "#9d174d", "#065f46"];
    const getAvatarColor = (id: number, idx: number) =>
        avatarColors[(id ?? idx) % avatarColors.length];

    // const visibleData = maxRows != null ? inboxData.slice(0, maxRows) : inboxData.slice(0, 5);   // CS-1984: guarded below
    const visibleData = maxRows != null ? (inboxData ?? []).slice(0, maxRows) : (inboxData ?? []).slice(0, 5);     // CS-1984
    const totalForViewAll = maxRows != null ? maxRows : 5;

    return (
        <Box
            sx={{
                backgroundColor: cardBg,
                borderRadius: 3,
                border: `1px solid ${borderColor}`,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: "100%",
            }}
        >
            <Box sx={{ px: 3, pt: 3, pb: 2.5, borderBottom: `1px solid ${rowBorder}` }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
                    <Typography
                        fontWeight={700}
                        sx={{
                        color: isDark ? "#e5e9ed" : "#1e293b",
                        fontSize: "1.15rem",
                        }}
                    >
                        Inbox
                    </Typography>
                    {headerAdornment}
                </Box>
                <Box sx={{ display: "inline-flex" }}>
                    {(tabArray).map((t, i) => (
                        <Box
                            key={t}
                            onClick={() => onTabChange(t)}
                            sx={{
                                px: 2.4,
                                py: 0.75,
                                cursor: "pointer",
                                fontSize: "0.84rem",
                                fontWeight: 500,
                                userSelect: "none",
                                borderRadius: i === 0
                                        ? "6px 0 0 6px"
                                        : i === tabArray.length - 1
                                        ? "0 6px 6px 0"
                                        : "0",
                                ...(i > 0 ? { marginLeft: "-1px" } : {}),
                                border:
                                    notifTab === t
                                        ? "1px solid #14b8a6"
                                        : `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"}`,
                                background: notifTab === t ? "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)" : "transparent",
                                color: notifTab === t ? "#fff" : isDark ? "#94a3b8" : "#64748b",
                                transition: "all 0.18s ease",
                                ...(i === 1 ? { marginLeft: "-1px" } : {}),
                            }}
                        >
                            {t.charAt(0).toUpperCase() + t.slice(1)}
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto" }}>
                {/* {inboxData.length === 0 ? (   // CS-1984: guarded below */}
                {(inboxData ?? []).length === 0 ? (     /* CS-1984 */
                    <Typography
                        sx={{ color: "#64748b", fontSize: "0.85rem", py: 5, textAlign: "center" }}
                    >
                        {notifTab === "unread"
                            ? "No unread messages to display."
                            : notifTab === "read"
                                ? "No read messages to display."
                                : "No sent messages to display."}
                    </Typography>
                ) : (
                    visibleData.map((n: any, idx: number) => {
                        const notification = n.notification;
                        let displayName = n.displayName || "User";
                        let displayMsg = n.displayMsg;
                        let displayDoc = n.displayDoc;

                        const isHighlighted = notifTab === 'unread';
                        return (
                            <Box
                                // key={notification.notificationId ?? idx}   CS-1984: guarded below
                                key={notification?.notificationId ?? idx}     // CS-1984
                                // onClick={() => navigate("/inbox_list", { state: { notificationType: notifTab, notificationId: notification.notificationId} })}   CS-1984: guarded below
                                onClick={() => navigate("/inbox_list", { state: { notificationType: notifTab, notificationId: notification?.notificationId} })}     // CS-1984
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    px: 2.5,
                                    py: 1.8,
                                    cursor: "pointer",
                                    borderBottom: `1px solid ${rowBorder}`,
                                    backgroundColor: isHighlighted && !isDashboardColor
                                        ? isDark ? "rgba(13,148,136,0.18)" : "rgba(20,184,166,0.07)"
                                        : "transparent",
                                    transition: "background-color 0.18s ease",
                                    "&:hover": {
                                        backgroundColor: isHighlighted
                                            ? isDark ? "rgba(13,148,136,0.26)" : "rgba(20,184,166,0.11)"
                                            : isDark ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.02)",
                                    },
                                    "&:last-child": { borderBottom: "none" },
                                }}
                            >
                                {/* Avatar — hidden when hideProfileImage is true */}
                                {!hideProfileImage && (
                                    displayDoc ? (
                                        <Box>
                                            {(() => {
                                                const src = buildAvatarSrc(displayDoc);
                                                return (
                                                    <Avatar
                                                        src={src}
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            fontWeight: "bold",
                                                            fontSize: "0.9rem",
                                                            bgcolor: src ? "transparent" : "#64748B",
                                                        }}
                                                    />
                                                );
                                            })()}
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: "50%",
                                                // backgroundColor: getAvatarColor(notification.senderId, idx),   CS-1984: guarded below
                                                backgroundColor: getAvatarColor(notification?.senderId, idx),     // CS-1984
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                                fontSize: "0.72rem",
                                                fontWeight: 700,
                                                color: "#fff",
                                                letterSpacing: "0.02em",
                                                mt: 0.2,
                                            }}
                                        >
                                            {getInitials(displayName)}
                                        </Box>
                                    )
                                )}

                                <Box flex={1} minWidth={0}>
                                    <Box
                                        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.45 }}
                                    >
                                        <Typography
                                            sx={{ fontSize: "0.88rem", fontWeight: 700, color: isDark ? "#e5e9ed" : "#1e293b" }}
                                        >
                                            {displayName}
                                        </Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexShrink: 0, ml: 1 }}>
                                            <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#64748b" : "#94a3b8" }}>
                                                {/* {formatInboxTime(notification.auditTimeStamp)}   CS-1984: guarded below */}
                                                {formatInboxTime(notification?.auditTimeStamp)}     {/* CS-1984 */}
                                            </Typography>
                                            {isHighlighted && (
                                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#14b8a6", flexShrink: 0 }} />
                                            )}
                                        </Box>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: "0.8rem",
                                            color: isDark ? "#64748b" : "#94a3b8",
                                            overflow: "hidden",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            lineHeight: 1.55,
                                        }}
                                    >
                                        {displayMsg?.replace(/<[^>]+>/g, "") || ""}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })
                )}
            </Box>

            {/* {inboxData.length > totalForViewAll && (   // CS-1984: guarded below */}
            {(inboxData ?? []).length > totalForViewAll && (     /* CS-1984 */
                <Box
                    sx={{
                        flexShrink: 0,
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        px: 2.5,
                        py: 1.2,
                        borderTop: `1px solid ${rowBorder}`,
                    }}
                >
                    <Box
                        onClick={() => navigate('/inbox_list', { state: { notificationType: notifTab } })}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.7,
                            px: 1.6,
                            py: 0.6,
                            borderRadius: "999px",
                            cursor: "pointer",
                            border: `1px solid ${isDark ? "rgba(20,184,166,0.22)" : "rgba(20,184,166,0.2)"}`,
                            backgroundColor: isDark ? "rgba(20,184,166,0.07)" : "rgba(20,184,166,0.05)",
                            transition: "all 0.2s ease",
                            "&:hover": {
                                backgroundColor: isDark ? "rgba(20,184,166,0.14)" : "rgba(20,184,166,0.1)",
                                borderColor: "rgba(20,184,166,0.5)",
                                transform: "translateX(2px)",
                                boxShadow: "0 2px 12px rgba(20,184,166,0.15)",
                            },
                            "&:active": { transform: "translateX(0px)" },
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                color: "#14b8a6",
                                letterSpacing: "0.02em",
                                lineHeight: 1,
                            }}
                        >
                            View All
                        </Typography>
                        <ArrowForwardIos sx={{ fontSize: 9, color: "#14b8a6" }} />
                    </Box>
                </Box>
            )}
        </Box>
    );
};